import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Copy,
  Check,
  FilterX,
  Search as SearchIcon,
  X,
} from "lucide-react";
import { useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import { getAdminCoupons, createCouponsBatch } from "../../../server/Coupon";
import { getAllCustomer } from "../../../server/Customer";
import {
  customersNotSent,
  planCouponBatchSends,
  readCouponBatchAttempts,
  releaseSettledCouponBatchOffer,
  settleCouponBatchAttempt,
  unsentBatchNotice,
  writeCouponBatchAttempts,
} from "../../../utils/couponBatchIdempotency";
import "./Coupons.scss";
import showAlert, { showAlertWithConfirm } from "../../../server/ShowAlert";

/** First page + each scroll page size (CPS `limit`; max 100, same idea as Payments). */
const COUPON_PAGE_SIZE = 100;
const SCROLL_THRESHOLD_PX = 400;
/** Matches CPS `BATCH_MAX_CUSTOMERS` — front chunks larger selections into multiple calls. */
const BATCH_CHUNK_SIZE = 50;

const openDatePicker = (event) => {
  const input = event.currentTarget;
  if (typeof input.showPicker !== "function") return;
  try {
    input.showPicker();
  } catch {
    // The picker is already open, or this browser only opens it from the icon.
  }
};

const customerDisplayName = (customer) => {
  const name = `${customer.firstName || ""} ${customer.lastName || ""}`.trim();
  return name || customer.email || String(customer.id);
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Admin-facing summary after batch create (partial success supported by CPS). */
const showBatchCreateResult = ({
  createdCount,
  replayedCount = 0,
  failed,
  chunkCount,
  unsentCustomers = [],
  unsentChunkCount = 0,
  hardError,
  fallbackError,
}) => {
  const failedList = Array.isArray(failed) ? failed : [];
  const failedCount = failedList.length;
  const unsentList = Array.isArray(unsentCustomers) ? unsentCustomers : [];
  const unsentCount = unsentList.length;
  const freshCount = Math.max(0, createdCount - replayedCount);

  if (createdCount === 0 && failedCount === 0 && unsentCount === 0) {
    showAlert({
      msgTitle: fallbackError || "יצירת הקופונים נכשלה",
      msgType: "error",
    });
    return;
  }

  const titleParts = [];
  if (chunkCount > 1) {
    if (unsentChunkCount > 0) {
      const sentCount = chunkCount - unsentChunkCount;
      titleParts.push(
        sentCount === 1
          ? `שליחה אחת מתוך ${chunkCount}`
          : `${sentCount} שליחות מתוך ${chunkCount}`,
      );
    } else {
      titleParts.push(`${chunkCount} שליחות`);
    }
  }
  if (freshCount > 0) {
    titleParts.push(
      freshCount === 1 ? "נוצר קופון אחד" : `נוצרו ${freshCount} קופונים`,
    );
  }
  if (replayedCount > 0 && freshCount === 0 && failedCount === 0) {
    titleParts.push("לא נוצרו קופונים חדשים");
  } else if (replayedCount > 0) {
    titleParts.push(
      replayedCount === 1
        ? "קופון אחד כבר היה קיים"
        : `${replayedCount} קופונים כבר היו קיימים`,
    );
  }
  if (failedCount > 0) {
    titleParts.push(
      failedCount === 1 ? "לקוח אחד נכשל" : `${failedCount} לקוחות נכשלו`,
    );
  }
  if (unsentCount === 1) {
    titleParts.push("לקוח אחד לא נשלח");
  } else if (unsentCount > 1) {
    titleParts.push(`${unsentCount} לקוחות לא נשלחו`);
  }

  const unsentNotice = unsentBatchNotice(unsentCount, unsentChunkCount);
  const unsentHtml = unsentNotice
    ? `<div style="text-align:right">${escapeHtml(unsentNotice)}</div>
        <div style="text-align:right;margin-top:12px;max-height:220px;overflow:auto">
          <div style="font-weight:600;margin-bottom:8px">לא נשלחו:</div>
          <ul style="padding-inline-start:18px;margin:0;font-size:13px;line-height:1.45">
            ${unsentList
              .slice(0, 30)
              .map((item) => {
                const who = item.email || item.customerId || "לקוח לא מזוהה";
                return `<li><strong>${escapeHtml(who)}</strong></li>`;
              })
              .join("")}
            ${
              unsentCount > 30
                ? `<li>ועוד ${unsentCount - 30} לקוחות…</li>`
                : ""
            }
          </ul>
        </div>`
    : "";

  const failedHtml =
    failedCount === 0
      ? ""
      : `<div style="text-align:right;margin-top:12px;max-height:220px;overflow:auto">
          <div style="font-weight:600;margin-bottom:8px">פירוט כשלונות:</div>
          <ul style="padding-inline-start:18px;margin:0;font-size:13px;line-height:1.45">
            ${failedList
              .slice(0, 30)
              .map((item) => {
                const who = item.email || item.customerId || "לקוח לא מזוהה";
                const reason = item.error || "שגיאה לא ידועה";
                return `<li><strong>${escapeHtml(who)}</strong> — ${escapeHtml(reason)}</li>`;
              })
              .join("")}
            ${
              failedCount > 30
                ? `<li>ועוד ${failedCount - 30} כשלונות…</li>`
                : ""
            }
          </ul>
        </div>`;

  const msgType =
    freshCount > 0 && replayedCount === 0 && failedCount === 0 && !hardError
      ? "success"
      : freshCount > 0 || replayedCount > 0
        ? "warning"
        : "error";

  const replayedHtml =
    freshCount === 0 && replayedCount > 0
      ? "<div style='text-align:right'>הבקשה זהה לבקשה שכבר בוצעה: אותם לקוחות, אותו סכום ואותו תוקף. הקופונים הקיימים לא נוצרו מחדש.</div>"
      : replayedCount > 0
        ? "<div style='text-align:right'>חלק מהקופונים כבר היו קיימים לאותם לקוחות ולא נוצרו מחדש.</div>"
        : "";

  showAlertWithConfirm({
    msgTitle: titleParts.join(" · ") || fallbackError || "תוצאת יצירה",
    msgHtml:
      `${replayedHtml}${unsentHtml}${failedHtml}` ||
      (freshCount > 0
        ? "<div style='text-align:right'>היצירה הושלמה בהצלחה</div>"
        : `<div style='text-align:right'>${escapeHtml(fallbackError || "יצירת הקופונים נכשלה")}</div>`),
    msgType,
    confirmText: "סגור",
  });
};

const STATUS_LABELS = {
  ACTIVE: "פעיל",
  USED: "מומש",
  PARTIALLY_USED: "מומש חלקית",
  INACTIVE: "מבוטל",
};

/** Matches CPS GET /coupons `status` values (tenant-wide list). */
const STATUS_FILTER_OPTIONS = [
  { value: "", label: "כל הסטטוסים" },
  { value: "ACTIVE", label: "פעיל" },
  { value: "USED", label: "מומש" },
  { value: "PARTIALLY_USED", label: "מומש חלקית" },
  { value: "INACTIVE", label: "מבוטל" },
];

const mapCpsCouponToRow = (coupon) => ({
  id: coupon.id,
  CouponCode: coupon.couponCode,
  Sum: coupon.sum,
  MinOrder: coupon.minOrder,
  ExpiredDate: coupon.expiredAt || "",
  status: coupon.status,
  Active: coupon.status === "ACTIVE",
  EmailAddress: coupon.email,
});

const formatExpiryDate = (value) => {
  if (!value) return "";
  return String(value).split("T")[0];
};

const statusLabel = (coupon) =>
  STATUS_LABELS[coupon.status] || (coupon.Active ? "פעיל" : "לא פעיל");

/**
 * CPS rules: `code` is exact lookup and cannot combine with status/limit/cursor.
 * Every admin fetch is scoped to the current station (`branch`).
 */
const buildAdminListParams = ({ status, code, branch }, { reset, cursor }) => {
  const station = String(branch || "").trim();
  const trimmedCode = (code || "").trim();
  if (trimmedCode) {
    return { code: trimmedCode, branch: station };
  }
  const params = { limit: COUPON_PAGE_SIZE, branch: station };
  if (status) params.status = status;
  if (!reset && cursor) params.cursor = cursor;
  return params;
};

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const [committedCode, setCommittedCode] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  /** `{ current, total }` while multi-chunk batch create is running. */
  const [saveProgress, setSaveProgress] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const tableScrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const nextCursorRef = useRef(null);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const browseFiltersRef = useRef({ status: "", code: "" });
  const searchCodeRef = useRef("");
  const fetchGenerationRef = useRef(0);
  const loadFirstPageRef = useRef(null);
  const saveInFlightRef = useRef(false);

  searchCodeRef.current = searchCode;

  const { stationId } = useSelector((state) => ({
    stationId: state.user.stationId,
  }));

  const syncHasMore = useCallback((more) => {
    setHasMore(more);
    hasMoreRef.current = more;
  }, []);

  const syncBrowseFilters = useCallback((filters) => {
    browseFiltersRef.current = filters;
    return filters;
  }, []);

  const fetchCouponsPage = useCallback(
    async (reset = false, filters = browseFiltersRef.current, generation) => {
      const params = buildAdminListParams(filters, {
        reset,
        cursor: nextCursorRef.current,
      });
      const res = await getAdminCoupons(params);
      if (
        generation !== undefined &&
        generation !== fetchGenerationRef.current
      ) {
        return;
      }
      const list = (res.data?.coupons ?? []).map(mapCpsCouponToRow);
      const nextCursor = filters.code?.trim()
        ? null
        : (res.data?.nextCursor ?? null);
      nextCursorRef.current = nextCursor;
      syncHasMore(Boolean(nextCursor));
      setCoupons((prev) => (reset ? list : [...prev, ...list]));
    },
    [syncHasMore],
  );

  const loadFirstPage = useCallback(
    async (filters = browseFiltersRef.current) => {
      const branch = stationId ? String(stationId) : "";
      const nextFilters = { ...filters, branch };
      const generation = ++fetchGenerationRef.current;
      syncBrowseFilters(nextFilters);
      setInitialLoading(true);
      nextCursorRef.current = null;
      syncHasMore(false);
      setCoupons([]);
      if (!branch) {
        setInitialLoading(false);
        return;
      }
      try {
        await fetchCouponsPage(true, nextFilters, generation);
      } catch (e) {
        if (generation !== fetchGenerationRef.current) return;
        console.error("getAdminCoupons failed", e);
        showAlert({
          msgTitle:
            e.response?.data?.error ||
            e.response?.data?.message ||
            "טעינת הקופונים נכשלה",
          msgType: "error",
        });
      } finally {
        if (generation === fetchGenerationRef.current) {
          setInitialLoading(false);
        }
      }
    },
    [stationId, fetchCouponsPage, syncBrowseFilters, syncHasMore],
  );

  loadFirstPageRef.current = loadFirstPage;

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    if (browseFiltersRef.current.code?.trim()) return;
    const generation = fetchGenerationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      await fetchCouponsPage(false, browseFiltersRef.current, generation);
    } catch (e) {
      if (generation !== fetchGenerationRef.current) return;
      console.error("getAdminCoupons load more failed", e);
      showAlert({
        msgTitle:
          e.response?.data?.error ||
          e.response?.data?.message ||
          "טעינת קופונים נוספים נכשלה",
        msgType: "error",
      });
    } finally {
      if (generation === fetchGenerationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [fetchCouponsPage]);

  useEffect(() => {
    loadFirstPageRef.current?.(browseFiltersRef.current);
  }, [stationId]);

  const commitCodeServerFilter = useCallback(() => {
    const code = searchCodeRef.current.trim();
    if (code === committedCode) return;
    setCommittedCode(code);
    if (code) {
      setFilterStatus("");
      loadFirstPage({ status: "", code });
      return;
    }
    loadFirstPage({ status: filterStatus, code: "" });
  }, [committedCode, filterStatus, loadFirstPage]);

  const clearCodeSearch = useCallback(() => {
    setSearchCode("");
    searchCodeRef.current = "";
    if (!committedCode) return;
    setCommittedCode("");
    loadFirstPage({ status: filterStatus, code: "" });
  }, [committedCode, filterStatus, loadFirstPage]);

  const handleStatusFilterChange = useCallback(
    (e) => {
      const status = e.target.value;
      setFilterStatus(status);
      setSearchCode("");
      searchCodeRef.current = "";
      setCommittedCode("");
      loadFirstPage({ status, code: "" });
    },
    [loadFirstPage],
  );

  const clearFilters = useCallback(() => {
    setSearchCode("");
    searchCodeRef.current = "";
    setCommittedCode("");
    setFilterStatus("");
    return loadFirstPage({ status: "", code: "" });
  }, [loadFirstPage]);

  const hasActiveFilters = Boolean(filterStatus || committedCode);
  useEffect(() => {
    getAllCustomer(stationId)
      .then((x) => setCustomers(x.data))
      .catch(() => console.log("error"));
  }, [stationId]);

  useEffect(() => {
    const scrollEl = tableScrollRef.current;
    const sentinel = sentinelRef.current;
    if (
      !scrollEl ||
      !sentinel ||
      !hasMore ||
      initialLoading ||
      !coupons.length
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some((entry) => entry.isIntersecting) &&
          !loadingMoreRef.current &&
          hasMoreRef.current
        ) {
          loadMore();
        }
      },
      { root: scrollEl, rootMargin: `${SCROLL_THRESHOLD_PX}px` },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadMore, coupons.length]);

  const saveCoupon = async (couponData) => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    try {
      if (!stationId) {
        showAlert({
          msgTitle: "לא נבחר סניף — לא ניתן ליצור קופון",
          msgType: "error",
        });
        return;
      }

      const selectedIds = Array.isArray(couponData.selectedCustomerIds)
        ? couponData.selectedCustomerIds.map(String)
        : [];

      if (selectedIds.length === 0) {
        showAlert({
          msgTitle: "יש לבחור לפחות לקוח אחד לקישור הקופון",
          msgType: "error",
        });
        return;
      }

      const batchCustomers = [];
      for (const id of selectedIds) {
        const customer = customers.find((c) => String(c.id) === id);
        if (!customer?.id || !customer?.email) {
          showAlert({
            msgTitle: "אחד או יותר מהלקוחות שנבחרו אינם תקינים (חסר מייל)",
            msgType: "error",
          });
          return;
        }
        batchCustomers.push({
          customerId: String(customer.id),
          email: String(customer.email).trim().toLowerCase(),
          name: customerDisplayName(customer),
        });
      }

      const baseBody = {
        branch: String(stationId),
        sum: Number(couponData.amount),
        minOrder: Number(couponData.minOrder),
        expiredAt: couponData.expiryDate,
      };
      const planned = planCouponBatchSends({
        base: baseBody,
        customers: batchCustomers,
        attempts: readCouponBatchAttempts(),
        createKey: () => crypto.randomUUID(),
        chunkSize: BATCH_CHUNK_SIZE,
      });
      // Key is stored before the request, so a timeout retry sends the same one.
      let attempts = planned.attempts;
      writeCouponBatchAttempts(attempts);

      setIsSaving(true);
      setSaveProgress({ current: 0, total: planned.sends.length });
      const allCreated = [...planned.alreadyCreated];
      const allFailed = [];
      let replayedCount = planned.alreadyCreated.length;
      let hardError = null;
      let stoppedSendIndex = null;

      try {
        for (let i = 0; i < planned.sends.length; i += 1) {
          const send = planned.sends[i];
          setSaveProgress({ current: i + 1, total: planned.sends.length });
          try {
            const res = await createCouponsBatch(
              send.body,
              send.idempotencyKey,
            );
            const created = Array.isArray(res.data?.created)
              ? res.data.created
              : [];
            const failed = Array.isArray(res.data?.failed)
              ? res.data.failed
              : [];
            attempts = settleCouponBatchAttempt(attempts, send.idempotencyKey, {
              created,
              failed,
            });
            writeCouponBatchAttempts(attempts);
            allCreated.push(...created);
            allFailed.push(...failed);
            if (res.data?.replayed === true) replayedCount += created.length;
          } catch (e) {
            hardError = e;
            stoppedSendIndex = i;
            for (const customer of send.body.customers) {
              allFailed.push({
                customerId: customer.customerId,
                email: customer.email,
                error:
                  e.response?.data?.error ||
                  e.response?.data?.message ||
                  "שגיאת שרת בשליחת המקטע",
              });
            }
            console.error("createCouponsBatch chunk failed", e);
            break;
          }
        }

        if (!hardError && allFailed.length === 0) {
          writeCouponBatchAttempts(
            releaseSettledCouponBatchOffer(attempts, baseBody),
          );
        }

        const createdTotal = allCreated.length;
        const unsentCustomers =
          stoppedSendIndex == null
            ? []
            : customersNotSent(planned.sends, stoppedSendIndex);
        const unsentChunkCount =
          stoppedSendIndex == null
            ? 0
            : planned.sends.length - stoppedSendIndex - 1;

        if (createdTotal > 0) {
          await clearFilters();
          setShowCreateForm(false);
          showBatchCreateResult({
            createdCount: createdTotal,
            replayedCount,
            failed: allFailed,
            chunkCount: planned.sends.length,
            unsentCustomers,
            unsentChunkCount,
            hardError: Boolean(hardError),
          });
          return;
        }

        showBatchCreateResult({
          createdCount: 0,
          replayedCount,
          failed: allFailed,
          chunkCount: planned.sends.length,
          unsentCustomers,
          unsentChunkCount,
          hardError: Boolean(hardError),
          fallbackError:
            hardError?.response?.data?.error ||
            hardError?.response?.data?.message ||
            allFailed[0]?.error ||
            "יצירת הקופונים נכשלה",
        });
      } finally {
        setIsSaving(false);
        setSaveProgress(null);
      }
    } finally {
      saveInFlightRef.current = false;
    }
  };
  const copyCouponCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="coupon-manager admin-page admin-page--fill">
      <div className="coupons-chrome">
        <header className="admin-page__header admin-page__header--sticky">
          <div className="admin-page__header-start">
            <h1 className="title-role">ניהול קופונים</h1>
          </div>
          <div className="admin-page__header-end">
            <button
              type="button"
              className="coupon-create-btn"
              onClick={() => setShowCreateForm(true)}
              title="הוסף קופון"
              aria-label="הוסף קופון"
            >
              <Plus aria-hidden size={18} strokeWidth={2} />
              <span>הוסף קופון</span>
            </button>
          </div>
        </header>

        <div className="coupons-filters admin-filter-bar admin-filter-bar--spread">
          <div className="admin-filter-bar__primary coupons-filters__row">
            <div className="search admin-search-affix">
              <input
                onChange={(e) => setSearchCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitCodeServerFilter();
                  }
                }}
                onBlur={(e) => {
                  if (e.relatedTarget?.closest?.(".coupons-filters")) return;
                  commitCodeServerFilter();
                }}
                value={searchCode}
                type="text"
                placeholder="קוד קופון..."
                className="admin-input"
                aria-label="חיפוש לפי קוד קופון"
              />
              {searchCode ? (
                <button
                  type="button"
                  className="admin-search-affix__clear"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={clearCodeSearch}
                  aria-label="נקה חיפוש קוד"
                  title="נקה חיפוש"
                >
                  <X aria-hidden size={18} strokeWidth={2} />
                </button>
              ) : (
                <span className="admin-search-affix__icon" aria-hidden>
                  <SearchIcon size={18} strokeWidth={2} />
                </span>
              )}
            </div>

            <select
              className="admin-input"
              value={filterStatus}
              onChange={handleStatusFilterChange}
              aria-label="סטטוס קופון"
            >
              {STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="clear-filters-btn admin-icon-btn admin-icon-btn--ghost admin-filter-end"
              onClick={clearFilters}
              title="נקה סינונים"
              aria-label="נקה סינונים"
              disabled={!hasActiveFilters}
            >
              <FilterX aria-hidden size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      <div className="admin-page__body coupons-body">
        {initialLoading ? (
          <div className="coupons-state coupons-state--loading">
            <p>טוען קופונים...</p>
          </div>
        ) : null}

        {!initialLoading && coupons.length === 0 ? (
          <div className="coupons-state coupons-state--empty">
            <p>
              {hasActiveFilters
                ? "לא נמצאו קופונים לפי הסינון"
                : "אין קופונים להצגה"}
            </p>
          </div>
        ) : null}

        {!initialLoading && coupons.length > 0 ? (
          <div className="coupons-table">
            <div className="coupons-table-scroll" ref={tableScrollRef}>
              <table>
                <thead>
                  <tr>
                    <th>קוד קופון</th>
                    <th>סכום</th>
                    <th>למימוש החל מ-</th>
                    <th>תוקף עד</th>
                    <th>סטטוס</th>
                    <th>מייל לקוח מקושר</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td data-label="קוד קופון">
                        <div className="coupon-code">
                          <span className="code">{coupon.CouponCode}</span>
                          <button
                            type="button"
                            className="copy-btn admin-icon-btn admin-icon-btn--ghost"
                            onClick={() => copyCouponCode(coupon.CouponCode)}
                            title="העתק קוד"
                            aria-label="העתק קוד קופון"
                          >
                            {copiedCode === coupon.CouponCode ? (
                              <Check size={14} />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td data-label="סכום">{coupon.Sum} ש"ח</td>
                      <td data-label="למימוש החל מ-">{coupon.MinOrder} ש"ח</td>
                      <td data-label="תוקף עד">
                        {formatExpiryDate(coupon.ExpiredDate)}
                      </td>
                      <td data-label="סטטוס">
                        <span className={`status ${coupon.Active}`}>
                          {statusLabel(coupon)}
                        </span>
                      </td>
                      <td data-label="מייל לקוח מקושר">
                        {coupon.EmailAddress}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                ref={sentinelRef}
                className="coupons-sentinel"
                aria-hidden="true"
              />

              {loadingMore ? (
                <div className="coupons-load-more">טוען קופונים נוספים...</div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {showCreateForm && (
        <PopUp
          close={() => {
            if (isSaving) return;
            setShowCreateForm(false);
          }}
          width={820}
        >
          <CouponFormModal
            customers={customers}
            isSaving={isSaving}
            saveProgress={saveProgress}
            onSave={saveCoupon}
            onClose={() => {
              if (isSaving) return;
              setShowCreateForm(false);
            }}
          />
        </PopUp>
      )}
    </div>
  );
};

const isEligibleCustomer = (customer) =>
  Boolean(customer?.id && String(customer.email || "").trim());

const CouponFormModal = ({
  customers,
  onSave,
  onClose,
  isSaving,
  saveProgress,
}) => {
  const [formData, setFormData] = useState({
    amount: "",
    minOrder: "",
    expiryDate: "",
    selectedCustomerIds: [],
  });
  const [customerSearch, setCustomerSearch] = useState("");

  const eligibleCustomers = customers.filter(isEligibleCustomer);

  const filteredCustomers = eligibleCustomers.filter((customer) => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      customer.firstName,
      customer.lastName,
      customer.email,
      customer.id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  const selectedSet = new Set(formData.selectedCustomerIds.map(String));
  const selectedCount = formData.selectedCustomerIds.length;
  const sendBatchCount = Math.max(
    1,
    Math.ceil(selectedCount / BATCH_CHUNK_SIZE),
  );
  const filteredSelectedCount = filteredCustomers.filter((c) =>
    selectedSet.has(String(c.id)),
  ).length;
  const allFilteredSelected =
    filteredCustomers.length > 0 &&
    filteredSelectedCount === filteredCustomers.length;

  const toggleCustomer = (customerId) => {
    const id = String(customerId);
    setFormData((prev) => {
      const ids = prev.selectedCustomerIds.map(String);
      if (ids.includes(id)) {
        return {
          ...prev,
          selectedCustomerIds: ids.filter((x) => x !== id),
        };
      }
      return { ...prev, selectedCustomerIds: [...ids, id] };
    });
  };

  const selectFilteredCustomers = () => {
    const filteredIds = filteredCustomers.map((c) => String(c.id));
    setFormData((prev) => {
      const existing = new Set(prev.selectedCustomerIds.map(String));
      const merged = [...prev.selectedCustomerIds.map(String)];
      for (const id of filteredIds) {
        if (existing.has(id)) continue;
        merged.push(id);
        existing.add(id);
      }
      return { ...prev, selectedCustomerIds: merged };
    });
  };

  const clearFilteredSelection = () => {
    const filteredIds = new Set(filteredCustomers.map((c) => String(c.id)));
    setFormData((prev) => ({
      ...prev,
      selectedCustomerIds: prev.selectedCustomerIds.filter(
        (id) => !filteredIds.has(String(id)),
      ),
    }));
  };

  const clearAllSelection = () => {
    setFormData((prev) => ({ ...prev, selectedCustomerIds: [] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSaving) return;
    if (!formData.amount || !formData.expiryDate || !formData.minOrder) {
      showAlert({
        msgTitle: "אנא מלא את כל השדות המסומנים בכוכבית",
        msgType: "error",
      });
      return;
    }
    if (formData.selectedCustomerIds.length === 0) {
      showAlert({
        msgTitle: "יש לבחור לפחות לקוח אחד לקישור הקופון",
        msgType: "error",
      });
      return;
    }
    onSave(formData);
  };

  const submitLabel =
    selectedCount <= 1
      ? "צור קופון"
      : sendBatchCount > 1
        ? `צור ${selectedCount} קופונים (${sendBatchCount} שליחות)`
        : `צור ${selectedCount} קופונים`;

  const savingLabel =
    saveProgress && saveProgress.total > 1
      ? `שולח ${saveProgress.current} מתוך ${saveProgress.total}...`
      : "יוצר...";

  return (
    <div className="coupon-modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-header__text">
            <h2>יצירת קופונים</h2>
            <p className="modal-header__hint">
              אותו סכום ותוקף — קופון נפרד לכל לקוח. מעל {BATCH_CHUNK_SIZE}{" "}
              לקוחות נשלח בכמה בקשות אוטומטית.
            </p>
          </div>
        </div>

        <div className="modal-form">
          <div className="form-row form-row--2">
            <div className="form-group">
              <label htmlFor="coupon-amount">סכום הקופון *</label>
              <input
                id="coupon-amount"
                type="number"
                min="1"
                className="price-input"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                placeholder="סכום בש״ח"
                required
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="coupon-min-order">מינימום לקנייה *</label>
              <input
                id="coupon-min-order"
                type="number"
                min="0"
                className="price-input"
                value={formData.minOrder}
                onChange={(e) =>
                  setFormData({ ...formData, minOrder: e.target.value })
                }
                placeholder="סכום בש״ח"
                required
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="coupon-expiry">תוקף עד *</label>
            <input
              id="coupon-expiry"
              className="coupon-expiry-input"
              type="date"
              value={formData.expiryDate}
              onChange={(e) =>
                setFormData({ ...formData, expiryDate: e.target.value })
              }
              onClick={openDatePicker}
              min={new Date().toISOString().split("T")[0]}
              required
              disabled={isSaving}
              title="לחיצה פותחת לוח שנה. אפשר גם להקליד את התאריך."
            />
          </div>

          <div className="form-group coupon-customers">
            <div className="coupon-customers__head">
              <label>לקוחות מקושרים *</label>
              <span className="coupon-customers__count">
                {selectedCount === 0
                  ? "לא נבחרו"
                  : sendBatchCount > 1
                    ? `${selectedCount} נבחרו · ${sendBatchCount} שליחות`
                    : `${selectedCount} נבחרו`}
              </span>
            </div>

            {selectedCount > 0 ? (
              <div className="coupon-customers__chips" aria-live="polite">
                {formData.selectedCustomerIds.map((id) => {
                  const customer = eligibleCustomers.find(
                    (c) => String(c.id) === String(id),
                  );
                  if (!customer) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      className="coupon-chip"
                      onClick={() => toggleCustomer(id)}
                      disabled={isSaving}
                      title="הסר בחירה"
                    >
                      <span>{customerDisplayName(customer)}</span>
                      <X aria-hidden size={14} strokeWidth={2} />
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="coupon-chip-clear"
                  onClick={clearAllSelection}
                  disabled={isSaving}
                >
                  נקה הכל
                </button>
              </div>
            ) : null}

            <div className="coupon-customers__toolbar">
              <div className="search admin-search-affix coupon-customers__search">
                <input
                  type="search"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="חיפוש שם או מייל..."
                  className="admin-input"
                  aria-label="חיפוש לקוחות"
                  disabled={isSaving}
                />
                {customerSearch ? (
                  <button
                    type="button"
                    className="admin-search-affix__clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setCustomerSearch("")}
                    aria-label="נקה חיפוש לקוחות"
                    title="נקה חיפוש"
                  >
                    <X aria-hidden size={18} strokeWidth={2} />
                  </button>
                ) : (
                  <span className="admin-search-affix__icon" aria-hidden>
                    <SearchIcon size={18} strokeWidth={2} />
                  </span>
                )}
              </div>
              <div className="coupon-customers__bulk">
                <button
                  type="button"
                  className="coupon-customers__bulk-btn"
                  onClick={
                    allFilteredSelected
                      ? clearFilteredSelection
                      : selectFilteredCustomers
                  }
                  disabled={isSaving || filteredCustomers.length === 0}
                >
                  {allFilteredSelected
                    ? "הסר מסוננים"
                    : customerSearch.trim()
                      ? "בחר תוצאות חיפוש"
                      : "בחר את כולם"}
                </button>
              </div>
            </div>

            <div
              className="coupon-customers__list"
              role="listbox"
              aria-multiselectable="true"
              aria-label="רשימת לקוחות לבחירה"
            >
              {filteredCustomers.length === 0 ? (
                <div className="coupon-customers__empty">
                  {eligibleCustomers.length === 0
                    ? "אין לקוחות זמינים עם כתובת מייל"
                    : "לא נמצאו לקוחות לחיפוש"}
                </div>
              ) : (
                filteredCustomers.map((customer) => {
                  const id = String(customer.id);
                  const checked = selectedSet.has(id);
                  return (
                    <label
                      key={id}
                      className={`coupon-customer-row${checked ? " is-selected" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCustomer(id)}
                        disabled={isSaving}
                      />
                      <span className="coupon-customer-row__body">
                        <span className="coupon-customer-row__name">
                          {customerDisplayName(customer)}
                        </span>
                        {customer.email ? (
                          <span className="coupon-customer-row__email">
                            {customer.email}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {isSaving && saveProgress ? (
            <div
              className="coupon-save-progress"
              role="status"
              aria-live="polite"
            >
              <div className="coupon-save-progress__track">
                <div
                  className="coupon-save-progress__bar"
                  style={{
                    width: `${Math.round(
                      (saveProgress.current / saveProgress.total) * 100,
                    )}%`,
                  }}
                />
              </div>
              <p className="coupon-save-progress__label">{savingLabel}</p>
            </div>
          ) : null}

          <div className="coupons-modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              ביטול
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? savingLabel : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Coupons;
